import { motion } from "framer-motion";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FloatingCart from "@/components/layout/FloatingCart";
import heroImage from "@/assets/hero-smoke.jpg";
import { useLanguage } from "@/context/LanguageContext";
import SEOHead from "@/components/SEOHead";

const OurStory = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Our Story"
        description="Learn about Bait Al-Teeb's heritage and passion for crafting luxury Arabian fragrances."
        canonical="https://bait-al-teeb-essence.lovable.app/story"
      />
      <Navbar />
      <FloatingCart />

      <main>
        {/* Hero Section */}
        <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src={heroImage} alt="Our Heritage" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-background/70" />
          </div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 text-center px-6">
            <span className="text-primary text-sm tracking-[0.3em] uppercase">{t.story.badge}</span>
            <h1 className="text-5xl md:text-7xl font-serif mt-4 mb-4">{t.story.title}</h1>
            <p className="text-xl text-muted-foreground font-serif arabic">{t.story.titleArabic}</p>
          </motion.div>
        </section>

        {/* Story Content */}
        <section className="section-padding">
          <div className="container-luxury max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-8 text-center">
              <h2 className="text-3xl md:text-4xl font-serif">{t.story.legacyTitle}</h2>
              <div className="divider-gold w-32 mx-auto" />
              <p className="text-muted-foreground leading-relaxed text-lg whitespace-pre-line">{t.story.legacyP1}</p>
              <p className="text-muted-foreground leading-relaxed text-lg whitespace-pre-line">{t.story.legacyP2}</p>
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="section-padding bg-card">
          <div className="container-luxury">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
              <span className="text-primary text-sm tracking-[0.3em] uppercase">{t.story.philosophyBadge}</span>
              <h2 className="text-3xl md:text-4xl font-serif mt-4">{t.story.philosophyTitle}</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {t.story.pillars.map((value: any, index: number) => (
                <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="text-center">
                  <h3 className="text-2xl font-serif mb-2">{value.title}</h3>
                  <p className="text-primary arabic mb-4">{value.arabic}</p>
                  <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Master Perfumer Section */}
        <section className="section-padding">
          <div className="container-luxury max-w-4xl text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-8">
              <span className="text-primary text-sm tracking-[0.3em] uppercase">{t.story.craftBadge}</span>
              <h2 className="text-3xl md:text-4xl font-serif">{t.story.craftTitle}</h2>
              <div className="divider-gold w-32 mx-auto" />
              <blockquote className="text-2xl font-serif italic text-foreground/80">{t.story.craftQuote}</blockquote>
              <p className="text-muted-foreground whitespace-pre-line">{t.story.craftAuthor}</p>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-gradient-to-b from-background to-card">
          <div className="container-luxury text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-serif mb-6">{t.story.ctaTitle}</h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto">{t.story.ctaDescription}</p>
              <a href="/collection" className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-amber-700 via-amber-600 to-yellow-600 text-amber-50 font-semibold tracking-wide rounded-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
                {t.story.ctaButton}
              </a>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OurStory;
